import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext.jsx';
import SectionEditModal from '../profile/SectionEditModal';
import {
  FEEDBACK_PLATFORMS,
  FEEDBACK_POST_TYPES,
} from '../../lib/feedbackPosts/types';
import { feedbackPlatformLabel, feedbackTypeLabel } from '../../lib/feedbackPosts/labels';
import { validateNewFeedbackPost } from '../../lib/feedbackPosts/validation';
import { updateFeedbackPost } from '../../lib/feedbackPosts/firestore';

const TYPE_OPTIONS = FEEDBACK_POST_TYPES.map((v) => ({
  value: v,
  label: feedbackTypeLabel(v),
}));

const PLATFORM_OPTIONS = FEEDBACK_PLATFORMS.map((v) => ({
  value: v,
  label: feedbackPlatformLabel(v),
}));

export default function EditFeedbackPostModal({ post, isOpen, onClose, onSuccess }) {
  const { user } = useAuth();
  const [feedbackType, setFeedbackType] = useState('general');
  const [platform, setPlatform] = useState('tiktok');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [contentUrl, setContentUrl] = useState('');
  const [feedbackPrompt, setFeedbackPrompt] = useState('');
  const [status, setStatus] = useState('open');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen && post) {
      setFeedbackType(post.feedbackType || 'general');
      setPlatform(post.platform || 'tiktok');
      setTitle(post.title || '');
      setDescription(post.description || '');
      setContentUrl(post.contentUrl || '');
      setFeedbackPrompt(post.feedbackPrompt || '');
      setStatus(post.status || 'open');
      setError(null);
    }
  }, [isOpen, post]);

  const handleSave = async () => {
    if (!user || !post || post.authorId !== user.uid) {
      setError('You can only edit your own post.');
      return;
    }
    if (submitting) return;

    const v = validateNewFeedbackPost({
      title,
      description,
      feedbackType,
      platform,
      contentUrl,
      feedbackPrompt,
    });
    if (!v.ok) {
      setError(v.message);
      return;
    }

    setError(null);
    setSubmitting(true);
    try {
      await updateFeedbackPost(post.id, user.uid, {
        title: title.trim(),
        description: description.trim(),
        feedbackType,
        platform,
        contentUrl: contentUrl.trim() || null,
        feedbackPrompt: feedbackPrompt.trim() || null,
        status,
      });
      onSuccess?.();
      onClose?.();
    } catch (err) {
      setError(err?.message || 'Could not update post. Try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!post) return null;

  return (
    <SectionEditModal
      isOpen={isOpen}
      onClose={onClose}
      title="Edit post"
      subtitle="Update your feedback request. Changes are saved immediately."
      onSave={handleSave}
      isSaving={submitting}
      errors={error ? { _general: error } : {}}
      saveLabel="Save changes"
    >
      <div className="space-y-4">
        <div>
          <label htmlFor="edit-fb-type" className="block text-xs font-medium text-slate-600">
            Feedback type <span className="text-red-500">*</span>
          </label>
          <select
            id="edit-fb-type"
            value={feedbackType}
            onChange={(e) => setFeedbackType(e.target.value)}
            className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-slate-400 focus:ring-slate-400"
          >
            {TYPE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="edit-fb-platform" className="block text-xs font-medium text-slate-600">
            Platform <span className="text-red-500">*</span>
          </label>
          <select
            id="edit-fb-platform"
            value={platform}
            onChange={(e) => setPlatform(e.target.value)}
            className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-slate-400 focus:ring-slate-400"
          >
            {PLATFORM_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="edit-fb-title" className="block text-xs font-medium text-slate-600">
            Title <span className="text-red-500">*</span>
          </label>
          <input
            id="edit-fb-title"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Hook review for 30s GRWM"
            maxLength={200}
            className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-slate-400 focus:ring-slate-400"
          />
        </div>

        <div>
          <label htmlFor="edit-fb-desc" className="block text-xs font-medium text-slate-600">
            Description <span className="text-red-500">*</span>
          </label>
          <textarea
            id="edit-fb-desc"
            rows={5}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Context, goal, and what you have already tried."
            className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:border-transparent resize-y"
          />
        </div>

        <div>
          <label htmlFor="edit-fb-url" className="block text-xs font-medium text-slate-600">
            Content URL (optional)
          </label>
          <input
            id="edit-fb-url"
            type="url"
            value={contentUrl}
            onChange={(e) => setContentUrl(e.target.value)}
            placeholder="https://…"
            className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-slate-400 focus:ring-slate-400"
          />
        </div>

        <div>
          <label htmlFor="edit-fb-prompt" className="block text-xs font-medium text-slate-600">
            What kind of feedback do you want? (optional)
          </label>
          <textarea
            id="edit-fb-prompt"
            rows={2}
            value={feedbackPrompt}
            onChange={(e) => setFeedbackPrompt(e.target.value)}
            placeholder="e.g. Be blunt on the first 3 seconds only."
            maxLength={500}
            className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:border-transparent resize-y"
          />
        </div>

        <div>
          <label htmlFor="edit-fb-status" className="block text-xs font-medium text-slate-600">
            Status
          </label>
          <select
            id="edit-fb-status"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-slate-400 focus:ring-slate-400"
          >
            <option value="open">Open</option>
            <option value="closed">Closed</option>
          </select>
        </div>
      </div>
    </SectionEditModal>
  );
}
